/**
 * SkillCatalog — all skill definitions with their effects.
 *
 * Each skill scales with level. Effects are registered as
 * ModifierBag modifiers and/or hooks at battle start.
 *
 * Weight controls how often a skill appears in the level-up choices.
 * Higher weight = more common. Roughly:
 *   10 = common, 6 = uncommon, 3 = rare
 */

import type { SkillDef } from "./SkillDef";
import * as S from "../stats/StatKeys";
import * as H from "../stats/HookKeys";

export const SKILL_WORD_POWER: SkillDef = {
  id: "word_power",
  name: "Word Power",
  description: "Long words (5+) deal +2 bonus damage per level",
  color: "#3b82f6",
  icon: "W",
  maxLevel: 5,
  weight: 10,
  apply: (bag, level) => {
    const bonus = level * 2;
    bag.addHook(H.ON_WORD_SCORED, (ctx: any) => {
      if (ctx.word.length >= 5) {
        return { damage: (ctx.damage ?? 0) + bonus };
      }
    }, `Word Power Lv${level}`);
  },
  descFn: (level) => `Long words (5+) deal +${level * 2} bonus damage`,
};

export const SKILL_THICK_SKIN: SkillDef = {
  id: "thick_skin",
  name: "Thick Skin",
  description: "Start each turn with +1 armor per level",
  color: "#6b7280",
  icon: "T",
  maxLevel: 5,
  weight: 10,
  apply: (bag, level) => {
    bag.add(S.ARMOR_FLAT, "flat", level, `Thick Skin Lv${level}`);
  },
  descFn: (level) => `+${level} armor per turn`,
};

export const SKILL_GOLD_FINDER: SkillDef = {
  id: "gold_finder",
  name: "Gold Finder",
  description: "Earn +25% gold from battles per level",
  color: "#fbbf24",
  icon: "$",
  maxLevel: 5,
  weight: 6,
  apply: (bag, level) => {
    bag.add(S.GOLD_PERCENT, "percent", level * 0.25, `Gold Finder Lv${level}`);
  },
  descFn: (level) => `+${level * 25}% gold from battles`,
};

export const SKILL_QUICK_STUDY: SkillDef = {
  id: "quick_study",
  name: "Quick Study",
  description: "Earn +25% XP from words per level",
  color: "#a78bfa",
  icon: "Q",
  maxLevel: 5,
  weight: 6,
  apply: (bag, level) => {
    bag.add(S.XP_PERCENT, "percent", level * 0.25, `Quick Study Lv${level}`);
  },
  descFn: (level) => `+${level * 25}% XP from words`,
};

export const SKILL_VITALITY: SkillDef = {
  id: "vitality",
  name: "Vitality",
  description: "Heal 1 HP at the start of each turn per level",
  color: "#22c55e",
  icon: "V",
  maxLevel: 5,
  weight: 8,
  apply: (bag, level) => {
    bag.add(S.HEAL_FLAT, "flat", level, `Vitality Lv${level}`);
    bag.addHook(H.ON_TURN_START, (_ctx: any) => {
      return { heal: level };
    }, `Vitality Lv${level}`);
  },
  descFn: (level) => `Heal ${level} HP at turn start`,
};

export const SKILL_PRECISION: SkillDef = {
  id: "precision",
  name: "Precision",
  description: "Short words (3 letters) deal +1 bonus damage per level",
  color: "#f97316",
  icon: "P",
  maxLevel: 5,
  weight: 8,
  apply: (bag, level) => {
    const bonus = level;
    bag.addHook(H.ON_WORD_SCORED, (ctx: any) => {
      if (ctx.word.length === 3) {
        return { damage: (ctx.damage ?? 0) + bonus };
      }
    }, `Precision Lv${level}`);
  },
  descFn: (level) => `3-letter words deal +${level} bonus damage`,
};

export const SKILL_SNEAK: SkillDef = {
  id: "sneak",
  name: "Sneak",
  description: "Reduces alert chance on adjacent nodes by 10% per level",
  color: "#475569",
  icon: "S",
  maxLevel: 5,
  weight: 3,
  apply: null, // No in-combat effect — purely out-of-combat
  getValue: (level, key) => {
    if (key === "alertReduction") return level * 0.1;
    return 0;
  },
  descFn: (level) => {
    const chance = Math.max(0, 50 - level * 10);
    return `−${level * 10}% alert chance (current: ${chance}%)`;
  },
};

/** All skill definitions for the catalog. */
export const ALL_SKILLS: SkillDef[] = [
  SKILL_WORD_POWER,
  SKILL_THICK_SKIN,
  SKILL_GOLD_FINDER,
  SKILL_QUICK_STUDY,
  SKILL_VITALITY,
  SKILL_PRECISION,
  SKILL_SNEAK,
];

/** Lookup by ID. */
export const SKILLS_BY_ID: ReadonlyMap<string, SkillDef> = new Map(
  ALL_SKILLS.map(s => [s.id, s])
);
